import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CUTIER_FIRST_MONTH_KEY } from '../utils/cutier/cutierLeadUtils';
import {
  CUTIER_INVOICE_PROFILE_TEMPLATE,
  type CutierInvoiceProfile,
} from '../types/cutierInvoiceProfile';

export interface CutierLead {
  id: string; // Firestore-Dokument-ID
  email: string; // normalisiert (lowercase, trimmed)
  name?: string;
  orderCount: number; // Anzahl Scheren/Bestellungen (Abrechnungseinheiten)
  createdAt: string; // ISO-String
  updatedAt?: string;
  monthKey: string; // YYYY-MM
  invite_sent: boolean;
  newsletterOptIn: 'none' | 'pending' | 'confirmed';
  source: 'single' | 'bulk';
}

export interface CutierStoreState {
  isPartnerAuthenticated: boolean;
  leads: CutierLead[];
  selectedMonth: string;
  isLoading: boolean;
  error: string | null;
  rateGross: number;
  seminarUrl: string;
  seminarPassword: string;
  invoiceProfile: CutierInvoiceProfile;
  invoiceProfileLoaded: boolean;

  setPartnerAuthenticated: (status: boolean) => void;
  setSelectedMonth: (monthKey: string) => void;
  setLeads: (leads: CutierLead[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAdminSettings: (settings: { rateGross: number; seminarUrl: string; seminarPassword: string }) => void;
  setInvoiceProfile: (profile: CutierInvoiceProfile) => void;
  setInvoiceProfileLoaded: (loaded: boolean) => void;
  reset: () => void;
}

const getCurrentMonthKey = () => {
  const now = new Date();
  const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return key < CUTIER_FIRST_MONTH_KEY ? CUTIER_FIRST_MONTH_KEY : key;
};

export const useCutierStore = create<CutierStoreState>()(
  persist(
    (set) => ({
      isPartnerAuthenticated: false,
      leads: [],
      selectedMonth: getCurrentMonthKey(),
      isLoading: false,
      error: null,
      rateGross: 50,
      seminarUrl: 'https://ramindell.com/online-seminar',
      seminarPassword: '',
      invoiceProfile: { ...CUTIER_INVOICE_PROFILE_TEMPLATE },
      invoiceProfileLoaded: false,

      setPartnerAuthenticated: (status) => set({ isPartnerAuthenticated: status }),
      setSelectedMonth: (monthKey) => set({ selectedMonth: monthKey }),
      setLeads: (leads) => set({ leads }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      setAdminSettings: (settings) =>
        set({
          rateGross: settings.rateGross,
          seminarUrl: settings.seminarUrl,
          seminarPassword: settings.seminarPassword,
        }),
      setInvoiceProfile: (profile) => set({ invoiceProfile: profile }),
      setInvoiceProfileLoaded: (loaded) => set({ invoiceProfileLoaded: loaded }),
      reset: () =>
        set({
          leads: [],
          selectedMonth: getCurrentMonthKey(),
          isLoading: false,
          error: null,
        }),
    }),
    {
      name: 'cutier-partner-session',
      // Nur Login-Status — wie PEBase-Session, ohne PIN zu speichern
      partialize: (state) => ({ isPartnerAuthenticated: state.isPartnerAuthenticated }),
    }
  )
);
